/**
 * AssetLoadService.js
 * 
 * Created on 1-okt-2013, 14:43:40
 *
 * The AssetLoadService can load manifests of assets and groups of assets that are defined in an
 * asset configuration. Asset configuration is found in assets/assets.json. The first time something
 * is loaded with the service it will first load the configuration and then load the necessary data.
 * 
 * TODO: setup system to load and merge multiple asset configuration file
 * TODO: manage groups that are already loaded and do not load them again
 * TODO: unload groups and functionality
 * 
 * @author Ivo Swartjes for Ranj
 **/

import * as log4javascript from 'log4javascript';
import Queue from "../../../../lib/html5-software-library/src/ranj/util/datastructures/queue/Queue";
import { isUndefined, isArray, isString, isNull } from "../../../../lib/html5-software-library/src/ranj/util/TypeUtil";
import AssetLoadServiceEvent from './AssetLoadServiceEvent';
import LanguagePack from "../../../../lib/html5-software-library/src/ranj/language/LanguagePack";
import ZipLoadQueue from './ZipLoadQueue';
import { clamp01 } from '../../../../lib/html5-software-library/src/ranj/util/MathUtil';
import { resolveVariablesInString } from '../../../../lib/html5-software-library/src/ranj/util/StringUtil';
import AssetUrlHelper from '../core/AssetUrlHelper';

class AssetLoadService {
    constructor(dispatcher, assetConfigurationManifest, assetLibrary, languagePack, config, appModel) {
        this._appModel = appModel;
        this._dispatcher = dispatcher;
        this._assetConfigurationManifest = assetConfigurationManifest;
        this._assetLibrary = assetLibrary;
        this._languagePack = languagePack;
        this._config = config;
        
        this._log = log4javascript.getLogger(this);
        this._version; // Will be set in the '_onAssetsConfigurationLoaded' method once the config.json file is parsed

        this._loader = new ZipLoadQueue(true);
        this._loader.setMaxConnections(5);
        this._loader.installPlugin(createjs.Sound);

        this._assetsConfigurationLoaded = false;
        this._assetsConfigurationLoading = false;
        this._assetsConfiguration = {};
        //strings are groups, arrays are manifests
        this._itemsToLoad = new Queue();
        // An array to keep all the groups that are currently loading
        // used to mark them as loaded on a complete
        this._itemsLoading = [];

        this._log.info("Loader:", this._loader);

        this._lazyLoadingImages = {};
    }

    getManifest(id) {
        return this._assetsConfiguration[id];
    }

    getProgress() {
        return this._loader.progress;
    }

    loadAssets(manifest) {
        if (Array.isArray(manifest)) {
            this._resolveVariablesInManifest(manifest);
        }
        //TODO: how to do variable resolution when parameter 'manifest' points to a manifest file to be loaded?
        if (this._assetsConfigurationLoaded) {
            //TODO: start loading of a group
            this._loader.loadManifest(manifest);
        } else {
            this._itemsToLoad.enqueue(manifest);

            //call loading the configuration, it will automatically load the enqueued items
            this._loadAssetsConfiguration();
        }
    }

    loadGroups() {
        if (this._assetsConfigurationLoaded) {
            var nothingToLoad = true;
            //Enqueue all the groups
            for (let i = 0; i < arguments.length; i++) {
                var group = arguments[i];
                var manifest = null;
                //Check if assets are a flash exported library file, or part of the assets configuration:
                if (isUndefined(lib[group]) || isUndefined(lib[group].properties) || isUndefined(lib[group].properties.manifest)) {
                    //Assets should be in the assets configuration
                    if (isUndefined(this._assetsConfiguration[group])) {
                        throw new Error("Asset group '" + group + "' was not found in configuration");
                    }

                    if (!this._assetsConfiguration[group].loaded) {
                        manifest = this._assetsConfiguration[group];
                    } else {
                        this._log.info("Asset group '" + group + "' is already loaded.");
                    }
                }
                else {
                    //Assets are in a flash exported file
                    //insert spritesheet metadata on spritesheet images to load
                    this._addSpritesheetMetadataToManifest(lib[group]);
                    this._assetsConfiguration[group] = lib[group].properties.manifest;
                    manifest = this._assetsConfiguration[group];
                }
                //Empty manifest will cause load error "PRELOAD_MANIFEST_EMPTY", so skip if empty
                if (manifest != null && manifest.length) {
                    this._resolveVariablesInManifest(manifest);
                    this._cacheBustManifest(manifest); // Append version number behind each source enable cache busting
                    this._loader.loadManifest(manifest);
                    this._itemsLoading.push(group);
                    nothingToLoad = false;
                } else {
                    this._log.info("Asset group '" + group + "' has no assets to load.");
                }
            }
            //Check if there is nothing to load
            if (nothingToLoad && this._itemsToLoad.isEmpty() && this._itemsLoading.length == 0) {
                //Call complete to dispatch an event
                this._onLoadComplete();
            }
        } else {
            //Asset Configuration not yet loaded, so load it first
            //  Enqueue all the groups that are not in the queue 
            for (let i = 0; i < arguments.length; i++) {
                if (!this._itemsToLoad.contains(arguments[i])) {
                    this._itemsToLoad.enqueue(arguments[i]);
                }
            }
            //Call loading the configuration, after which the enqueued groups will be automatically loaded
            this._loadAssetsConfiguration();
        }
    }

    loadGroup(group) {
        this.loadGroups(group);
    }

    _loadAllQueuedItems() {
        var item;
        while (!this._itemsToLoad.isEmpty()) {
            item = this._itemsToLoad.dequeue();
            if (isArray(item)) {
                this.loadAssets(item);
            } else if (isString(item)) {
                this.loadGroup(item);
            }
        }
    }

    _loadAssetsConfiguration() {
        if (this._assetsConfigurationLoaded || this._assetsConfigurationLoading)
            return;

        this._assetsConfigurationLoading = true;

        this._loader.addEventListener("complete", this._onAssetsConfigurationLoaded.bind(this));
        this._loader.addEventListener("fileload", this._onLoadFileComplete.bind(this));
        this._loader.loadManifest(this._assetConfigurationManifest);
    }

    _onAssetsConfigurationLoaded() {
        this._assetsConfigurationLoaded = true;
        this._assetsConfigurationLoading = false;

        // Remember the version of the build for cache busting.
        // The NGTConfig class is or may be not populated yet at this point
        // This means that a simple 'this._config.getValue('general.build')' won't work,
        // so we take the raw JSON from config.json
        this._version = this._assetLibrary.getAsset("config").general.build;

        //TODO: give a clear error message for parse errors in the json files

        this._addAssetsConfiguration(this._assetLibrary.getAsset("assets"));

        this._log.info("Assets configuration loaded: " + this.assetsConfiguration);

        //Initialize zip loading if enabled in config
        const ziploadingEnabled = this._assetLibrary.getAsset("config").settings.zipLoadingEnabled;
        this._log.info("Zip loading enabled: " + ziploadingEnabled);
        if (ziploadingEnabled) {
            this._initializeZipLoading();
        }

        //Setup all 'normal' eventlistensers
        this._loader.removeAllEventListeners();
        this._loader.addEventListener("complete", this._onLoadComplete.bind(this));
        this._loader.addEventListener("fileload", this._onLoadFileComplete.bind(this));
        this._loader.addEventListener("error", this._onLoadError.bind(this));
        this._loader.addEventListener("progress", this._onLoadProgress.bind(this));

        this._loadAllQueuedItems();
    }

    _addAssetsConfiguration(assetConfiguration) {
        if (isUndefined(assetConfiguration))
            return;

        //Merge the groups from the configuration into the main configuration
        for (var group in assetConfiguration) {
            const existingGroup = this._assetsConfiguration[group];
            if (isUndefined(existingGroup)) {
                this._assetsConfiguration[group] = assetConfiguration[group];
            } else {
                for (var i = 0; i < assetConfiguration[group].length; i++) {
                    existingGroup.push(assetConfiguration[group][i]);
                }
            }
        }
    }

    _onLoadFileComplete(event) {
        var o = event.item;

        //this._log.info("Load file complete. id:", o.id, "result:", event.result);
        var id = o.id;

        // Add to the game's asset library
        this._assetLibrary.addAsset(o.id, event.result);

        if (o.type === createjs.Types.IMAGE) {
            // Exported FLA libraries require the images to be in the
            // "images" global scope object.
            window.images[id] = event.result;
            // If spritesheetMetadata was set on object during loadGroup call, apply it
            this._applyImageSpritesheetMetadata(o, event.result);
        }
        /*else if (o.ext === "xml") {
         var xml = ranj.XMLUtil.fromXML(event.result, true);
         this._assetLibrary.addAsset(o.id, xml);
         }*/
        else if (o.ext === "lp") { //TODO: because lp files have different extensions they are not parsed automatically
            this._addLanguage(event.result, o.src);

        } else if (o.type === createjs.Types.JSON) {
            // If languagepack JSON, add to language pack
        }
    }

    _onLoadComplete(event) {
        var item;
        for (var i = 0; i < this._itemsLoading.length; i++) {
            item = this._itemsLoading[i];
            if (isString(item)) {
                this._assetsConfiguration[item].loaded = true;
            }
        }
        this._itemsLoading = [];

        this._log.info("Load complete.");
        this._dispatcher.dispatch(AssetLoadServiceEvent.LOAD_COMPLETE);
    }

    _onLoadProgress(event) {
        //this._log.info("Load progress", event.progress);     
        this._dispatcher.dispatch(AssetLoadServiceEvent.LOAD_PROGRESS, { progress: clamp01(event.progress) });
    }

    _onLoadError(event) {
        this._log.error("Load file error: ", event.title);
        this._dispatcher.dispatch(AssetLoadServiceEvent.LOAD_FILE_ERROR);
    }

    _addLanguage(jsonObj, src) {
        var nwLanguagePack = new LanguagePack();
        nwLanguagePack.setPropertiesFromObject(jsonObj);
        this._languagePack.merge(nwLanguagePack, true);
    }

    _cacheBustManifest(manifest) {
        // Append the version of the game behind each source to enable cache busting 
        const postfix = "v=" + this._version;
        manifest.forEach(function (item) {
            const sep = item.src.indexOf("?") > -1 ? "&" : "?"
            item.src += sep + postfix;
        });
        return manifest;
    }

    /// ----
    /// Lazy loading
    /// ----

    lazyLoadImage(id, url, onLoadCallback, group) {
        group = isNull(group) ? "_default_" : group;

        // check if image is already loaded into the asset library
        if (this._assetLibrary.hasAsset(id)) {
            if (!isNull(onLoadCallback)) {
                onLoadCallback(id);
            }
        } else {
            // check if there is already a group
            var lazyLoadingGroup = this._lazyLoadingImages[group];
            if (isNull(lazyLoadingGroup)) {
                lazyLoadingGroup = this._lazyLoadingImages[group] = {};
            }

            // check if this image is currently under lazy loading, if so, the 'onload' callback will be overriden
            // if not, add this lazy loading image to the cache object
            // -- this properly handles concurrent lazy loading
            var lazyLoadedImage = isNull(lazyLoadingGroup[id]) ? (lazyLoadingGroup[id] = new Image()) : lazyLoadingGroup[id];

            // TODO - in case of CDN?
            //this._lazyLoadedImage.crossOrigin = "Anonymous";

            // insert callback into the lazy loading image
            // -- this insert is useful as it allows to cancel the callback at any moment by setting it to null
            lazyLoadedImage.callback = onLoadCallback;

            // set callback when image is loaded
            lazyLoadedImage.onload = function () {
                // add it to asset library
                this._assetLibrary.addAsset(id, new createjs.Bitmap(lazyLoadedImage));
                // clean up cached lazy loading
                lazyLoadingGroup[id] = null;
                delete lazyLoadingGroup[id];
                // call callback
                if (!isNull(lazyLoadedImage.callback)) {
                    lazyLoadedImage.callback(id);
                }
            }.bind(this);

            // set url source to lazy load
            lazyLoadedImage.src = url;
        }
    }

    cancelLazyLoadingCallbackForImage(imageID, group) {
        group = isNull(group) ? "_default_" : group;
        var lazyLoadingGroup = this._lazyLoadingImages[group];
        // set callback to lazy loading image to 'null'
        if (!isNull(lazyLoadingGroup) && !isNull(lazyLoadingGroup[imageID])) {
            lazyLoadingGroup[imageID].callback = null;
            lazyLoadingGroup[imageID] = null;
            delete lazyLoadingGroup[imageID];
        }
    }

    cancelLazyLoadingCallbacksForGroup(group) {
        group = isNull(group) ? "_default_" : group;
        var lazyLoadingGroup = this._lazyLoadingImages[group];
        if (!isNull(lazyLoadingGroup)) {
            // sets all callbacks to lazy loading images from this group to 'null'
            for (var key in lazyLoadingGroup) {
                lazyLoadingGroup[key].callback = null;
                lazyLoadingGroup[key] = null;
            }
            // remove group
            this._lazyLoadingImages[group] = null;
            delete this._lazyLoadingImages[group];
        }
    }


    // Spritesheet methods

    /**
     * Add spritesheet metadata to items to load, where applicable.
     * This is done so after loading the item, the spritesheet can be instantiated based on the metadata.
     * Also see AssetLoadService._applyImageSpritesheetMetadata
     * To use spritesheets, enable spritesheet export in the asset fla's publish settings.
     * @param Object group Exported asset library.
     * @returns {undefined}
     */
    _addSpritesheetMetadataToManifest(group) {
        var manifest = group.properties.manifest;
        var ssMetadata = group.ssMetadata;
        //go through manifest and add spritesheet meta data to spritesheet image entries
        if (ssMetadata != null && ssMetadata.length) {
            var ssMetadataById = {};
            for (let i = 0; i < ssMetadata.length; i++) {
                ssMetadataById[ssMetadata[i].name] = ssMetadata[i];
            }
            for (let i = 0; i < manifest.length; i++) {
                var entry = manifest[i];
                if (ssMetadataById[entry.id] != null) {
                    entry.spritesheetMetadata = ssMetadataById[entry.id];
                }
            }
        }
    }

    /**
     * Check if loaded item has spritesheetMetadata 
     *  If so, instantiate a spritesheet based on the metadata
     * Also see AssetLoadService._addSpritesheetMetadataToManifest.
     * @param LoadItem item Object with the item properties for the image that was loaded.
     * @param HTMLImage image The image data that was loaded
     * @returns {undefined}
     */
    _applyImageSpritesheetMetadata(item, image) {
        //If spritesheetMetadata was set on object during loadGroup call, 
        //  create spritesheet and add to "ss" global scope object.
        //Should be called after loading the image item, so window.images contains the image
        var ssMeta = item.spritesheetMetadata;
        if (ssMeta != null) {
            window.ss[ssMeta.name] = new createjs.SpriteSheet({ "images": [image], "frames": ssMeta.frames })
        }
    }

    _initializeZipLoading() {
        const ziploadingFolder = this._assetLibrary.getAsset("config").settings.zipLoadingFolder;
        this._loader.initializeZipLoading(ziploadingFolder);
    }

    /**
     * Resolve the variables in the src props of each entry in manifest.
     * We do this in place, so method getManifest will return the manifest with resolved variables.
     * @param {Array<Object>} manifest 
     */
    _resolveVariablesInManifest(manifest) {
        return manifest.map(e => {
            e.src = AssetUrlHelper.resolvePath(e.src, this._appModel.language);
            return e;
        });
    }

    _resolveVariablesInPath(path) {
        if (this._variablesToResolve) {
            return resolveVariablesInString(path, this._variablesToResolve);
        }
        return path;
    }
}

export default AssetLoadService;